import { HdGateGraphLayout, HdGateGraphNode } from './hd-gate-graph.models';
import { circleSubpath, edgeSubpath } from './hd-gate-graph-scene';

/** Eén gebatchte laag van de deeltak onder de knoop waarop je hovert. */
export interface HdGateGraphFocusLayerVm {
  readonly layer: number;
  readonly circles: string;
  readonly edges: string;
}

export interface HdGateGraphFocusVm {
  readonly id: string;
  /** Van de poort tot en met de knoop zelf. */
  readonly ancestors: readonly HdGateGraphNode[];
  /** Connectoren langs het voorouderpad. */
  readonly ancestorEdges: readonly { readonly id: string; readonly d: string; readonly layer: number }[];
  /**
   * De deeltak per laag gebatcht. De cirkels van laag 1 en 2 staan hier
   * bewust óók in, maar worden door de component niet uit deze paths getekend:
   * die twee lagen dragen tekst en komen als losse elementen terug via
   * {@link lines} en {@link colours}.
   */
  readonly subtree: readonly HdGateGraphFocusLayerVm[];
  /** Lijncirkels in de deeltak — dragen nummer en keynote. */
  readonly lines: readonly HdGateGraphNode[];
  /** Kleurcirkels in de deeltak — dragen hun cijfer. */
  readonly colours: readonly HdGateGraphNode[];
}

/**
 * Zoekt de knoop onder een punt in viewBox-coördinaten.
 *
 * Brute force over alle 1339 knopen: dat kost minder dan een tiende
 * milliseconde en draait hoogstens één keer per animatieframe, dus een
 * ruimtelijke index zou alleen maar complexiteit toevoegen. Bij overlappende
 * treffers wint de diepste laag — de kleine bolletjes liggen visueel bovenop.
 */
export function findHdGateGraphNodeAt(
  layout: HdGateGraphLayout,
  x: number,
  y: number,
): HdGateGraphNode | undefined {
  let best: HdGateGraphNode | undefined;
  let bestSlack = Infinity;
  for (const node of layout.nodes) {
    // Afstand tot de RAND, niet tot het middelpunt. Daardoor wint een klein
    // basis-bolletje van de grote cirkels waarbinnen het ligt, en hoeft de
    // muis niet exact op een cirkel van 1,4 eenheden te staan.
    const slack = Math.hypot(node.x - x, node.y - y) - node.radius;
    if (slack > HIT_TOLERANCE) continue;
    if (slack < bestSlack || (slack === bestSlack && node.layer > (best?.layer ?? -1))) {
      best = node;
      bestSlack = slack;
    }
  }
  return best;
}

/**
 * Hoeveel een aanwijzer naast een cirkel mag zitten en die toch raken, in
 * layout-eenheden. Zonder speling zijn de basis-bolletjes (straal 1,4) op
 * fit-schaal praktisch onaanwijsbaar.
 */
const HIT_TOLERANCE = 1.6;

/**
 * Bouwt de focus-overlay voor één knoop: het voorouderpad naar de poort plus de
 * volledige deeltak eronder.
 *
 * De deeltak wordt per laag in één path gebatcht, dus de overlay bestaat uit
 * hooguit zes paths — even goedkoop of je nu op een lijn (222 nakomelingen) of
 * op een toon (5) staat.
 */
export function buildHdGateGraphFocus(node: HdGateGraphNode): HdGateGraphFocusVm {
  const ancestors: HdGateGraphNode[] = [];
  for (let current: HdGateGraphNode | undefined = node; current; current = current.parent) {
    ancestors.unshift(current);
  }

  const ancestorEdges = ancestors.slice(1).map(child => ({
    id: child.id,
    layer: child.layer,
    d: edgeSubpath(child.parent!, child),
  }));

  const byLayer = new Map<number, HdGateGraphNode[]>();
  const stack = [...node.children];
  while (stack.length > 0) {
    const current = stack.pop()!;
    const list = byLayer.get(current.layer) ?? [];
    list.push(current);
    byLayer.set(current.layer, list);
    for (const child of current.children) stack.push(child);
  }

  const subtree = [...byLayer.keys()]
    .sort((a, b) => b - a)
    .map(layer => {
      const nodes = byLayer.get(layer)!;
      return {
        layer,
        circles: nodes.map(circleSubpath).join(''),
        edges: nodes.map(child => edgeSubpath(child.parent!, child)).join(''),
      };
    });

  return {
    id: node.id,
    ancestors,
    ancestorEdges,
    subtree,
    lines: byLayer.get(1) ?? [],
    colours: byLayer.get(2) ?? [],
  };
}
